import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Grid,
} from '@mui/material';

// Generic skeleton components
export const TextSkeleton: React.FC<{ lines?: number; width?: string | number }> = ({ 
  lines = 1, 
  width = '100%' 
}) => (
  <Stack spacing={1}>
    {Array.from({ length: lines }).map((_, index) => (
      <Skeleton
        key={index}
        variant="text"
        width={index === lines - 1 ? width : '100%'}
        height={20}
        animation="wave"
      />
    ))}
  </Stack>
);

export const CardSkeleton: React.FC<{ height?: number }> = ({ height = 200 }) => (
  <Card>
    <CardContent>
      <Stack spacing={2}>
        <Skeleton variant="text" width="60%" height={24} animation="wave" />
        <Skeleton variant="rectangular" width="100%" height={height} animation="wave" />
        <Stack direction="row" spacing={1}>
          <Skeleton variant="text" width="30%" height={16} animation="wave" />
          <Skeleton variant="text" width="40%" height={16} animation="wave" />
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

export const AvatarSkeleton: React.FC<{ size?: number }> = ({ size = 40 }) => (
  <Skeleton variant="circular" width={size} height={size} animation="wave" />
);

export const ButtonSkeleton: React.FC<{ width?: string | number }> = ({ width = 100 }) => (
  <Skeleton variant="rounded" width={width} height={36} animation="wave" />
);

// Specific skeleton layouts for common UI patterns
export const PetCardSkeleton: React.FC = () => (
  <Card>
    <CardContent>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <AvatarSkeleton size={60} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="70%" height={24} animation="wave" />
            <Skeleton variant="text" width="50%" height={16} animation="wave" />
          </Box>
        </Stack>
        <Skeleton variant="rectangular" width="100%" height={120} animation="wave" />
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <ButtonSkeleton width={80} />
          <ButtonSkeleton width={80} />
          <ButtonSkeleton width={80} />
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

export const DashboardSkeleton: React.FC = () => (
  <Stack spacing={3}>
    {/* Header */}
    <Box>
      <Skeleton variant="text" width="40%" height={32} animation="wave" />
      <Skeleton variant="text" width="60%" height={20} animation="wave" />
    </Box>

    {/* Stats Cards */}
    <Grid container spacing={2}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Card>
            <CardContent>
              <Stack spacing={1}>
                <Skeleton variant="text" width="70%" height={16} animation="wave" />
                <Skeleton variant="text" width="40%" height={28} animation="wave" />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>

    {/* Main Content */}
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <CardSkeleton height={300} />
      </Grid>
      <Grid item xs={12} md={4}>
        <Stack spacing={2}>
          <CardSkeleton height={140} />
          <CardSkeleton height={140} />
        </Stack>
      </Grid>
    </Grid>
  </Stack>
);

export const ListSkeleton: React.FC<{ items?: number }> = ({ items = 5 }) => (
  <Stack spacing={2}>
    {Array.from({ length: items }).map((_, index) => (
      <Card key={index}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <AvatarSkeleton />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={20} animation="wave" />
              <Skeleton variant="text" width="80%" height={16} animation="wave" />
            </Box>
            <ButtonSkeleton width={60} />
          </Stack>
        </CardContent>
      </Card>
    ))}
  </Stack>
);

export const FormSkeleton: React.FC = () => (
  <Card>
    <CardContent>
      <Stack spacing={3}>
        <Skeleton variant="text" width="30%" height={24} animation="wave" />
        
        {/* Form fields */}
        {Array.from({ length: 4 }).map((_, index) => (
          <Box key={index}>
            <Skeleton variant="text" width="20%" height={16} animation="wave" sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" width="100%" height={56} animation="wave" />
          </Box>
        ))}
        
        {/* Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <ButtonSkeleton width={80} />
          <ButtonSkeleton width={100} />
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

export const CalendarSkeleton: React.FC = () => (
  <Card>
    <CardContent>
      <Stack spacing={2}>
        {/* Calendar header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Skeleton variant="text" width="30%" height={28} animation="wave" />
          <Stack direction="row" spacing={1}>
            <ButtonSkeleton width={40} />
            <ButtonSkeleton width={40} />
          </Stack>
        </Stack>
        
        {/* Calendar grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
          {Array.from({ length: 42 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              height={40}
              animation="wave"
              sx={{ borderRadius: 1 }}
            />
          ))}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({ 
  rows = 5, 
  columns = 4 
}) => (
  <Card>
    <CardContent>
      <Stack spacing={2}>
        {/* Table header */}
        <Stack direction="row" spacing={2}>
          {Array.from({ length: columns }).map((_, index) => (
            <Skeleton
              key={index}
              variant="text"
              width="100%"
              height={20}
              animation="wave"
            />
          ))}
        </Stack>
        
        {/* Table rows */}
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <Stack key={rowIndex} direction="row" spacing={2}>
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton
                key={colIndex}
                variant="text"
                width="100%"
                height={16}
                animation="wave"
              />
            ))}
          </Stack>
        ))}
      </Stack>
    </CardContent>
  </Card>
);

// Higher-order component for adding skeleton loading to any component
interface WithSkeletonProps {
  loading?: boolean;
  skeleton?: React.ReactNode;
  children: React.ReactNode;
}

export const WithSkeleton: React.FC<WithSkeletonProps> = ({ 
  loading = false, 
  skeleton, 
  children 
}) => {
  if (loading) {
    return <>{skeleton || <CardSkeleton />}</>;
  }
  return <>{children}</>;
};